/* eslint-disable @typescript-eslint/no-shadow */
import Vue from 'vue';
import axios from 'axios';
import JobPostingsService from '~/services/JobPostingsService';
import BugsnagWrapper from '~/services/BugSnagService';
import * as experiments from '~/constants/experiments';
import { runLocalToggleOverrideInitializeEffect } from './effects/runLocalToggleOverrideInitializeEffect';
import { runOneTrustEffect } from './effects/runOneTrustEffect';
import { getExperimentById } from './getters/getExperimentById';
import storeMutations from './constants/storeMutations';
import navTypes from './constants/navTypes';
import { refreshCartProductPrices } from './actions/refreshCartProductPrices';

export const namespaced = false;

export const state = () => ({
  pageTransition: false,
  navType: navTypes.primary,
  navigationData: null,
  suppressModal: false,
  isSliderVisible: false,
  showEmailInput: true,
  experimentsInitialized: {},
  isGrowthbookInitialized: false,
  experiments: [],
  featureFlags: [],
  showShopButton: false,
  appInitialized: false,
  jobPostings: [],
  appStoreReviewsCount: '10,000+',
  isInStoryblok: false,
  currentRoute: {},
  packagePageTotal: 0,
  geoLocationCountry: null,
  products: [],
  productsCollection: [],
  collections: {}, // Store multiple collections by name/id
  shippingZones: [],
  isShopifyOn: process.env.NUXT_ENV_SHOPIFY_ON === 'true' ?? false,
  appVersion: process.env.appVersion ?? '0.0.0',
  packageBuilder: {
    package: {},
    sizeVariant: {},
  },
  subscriptionDisplayPrices: {}, // { 'key|CAD': 50 }
});

export const mutations = {
  [storeMutations.SET_NAV_TYPE](state, type) {
    state.navType = type;
  },
  [storeMutations.SET_NAV_DATA](state, data) {
    state.navigationData = data;
  },
  [storeMutations.SET_MODAL_SUPPRESSION](state, boolean) {
    state.suppressModal = boolean;
  },
  [storeMutations.SET_SLIDER_VISIBLE](state, boolean) {
    state.isSliderVisible = boolean;
  },
  [storeMutations.TOGGLE_SHOW_EMAIL_INPUT](state) {
    state.showEmailInput = !state.showEmailInput;
  },
  [storeMutations.SET_SHOW_EMAIL_INPUT](state, boolean) {
    state.showEmailInput = boolean;
  },
  [storeMutations.REGISTER_EXPERIMENT](state, experiment) {
    if (experiment) {
      state.experimentsInitialized[experiment.id] = true;
      state.experiments.push(experiment);
    }
  },
  [storeMutations.HIDE_SHOP_BUTTON](state, boolean) {
    state.showShopButton = !boolean;
  },
  [storeMutations.ADD_JOB_POSTINGS](state, jobPostings) {
    state.jobPostings = jobPostings;
  },
  SET_APP_INITIALIZED(state, boolean) {
    state.appInitialized = boolean;
  },
  [storeMutations.IS_IN_STORYBLOK](state, boolean) {
    state.isInStoryblok = boolean;
  },
  [storeMutations.SET_CURRENT_ROUTE](state, route) {
    state.currentRoute = route;
  },
  [storeMutations.SET_PACKAGE_PAGE_TOTAL](state, total) {
    state.packagePageTotal = total;
  },
  [storeMutations.SET_GEOLOCATION_COUNTRY](state, country) {
    state.geoLocationCountry = country;
  },
  [storeMutations.REGISTER_GROWTHBOOK_EXPERIMENTS](state, experiment) {
    state.experiments.push(experiment);
    if (process.client && window && window.clarity) {
      window.clarity('set', experiment.id, experiment.variant);
    }
  },
  [storeMutations.SET_GROWTHBOOK_INITIALIZED](state, boolean) {
    state.isGrowthbookInitialized = boolean;
  },
  [storeMutations.SET_FEATURE_FLAG](state, { key, isOn }) {
    state.featureFlags.push({ key, isOn });
  },
  SET_PRODUCTS(state, products) {
    state.products = products;
  },
  SET_COLLECTION(state, collection) {
    state.productsCollection = collection;
  },
  UPDATE_PRODUCTS_IN_COLLECTION(state, updatedProducts) {
    // Update only specific products in the collection without replacing the entire array
    // This prevents cascade re-renders throughout the app
    if (!Array.isArray(updatedProducts) || updatedProducts.length === 0) return;

    updatedProducts.forEach(updatedProduct => {
      const index = state.productsCollection.findIndex(p => p.id === updatedProduct.id);
      if (index !== -1) {
        // Use Vue.set for reactivity
        Vue.set(state.productsCollection, index, updatedProduct);
      }
    });
  },
  SET_NAMED_COLLECTION(state, { name, collection }) {
    state.collections[name] = collection;
  },
  SET_SHIPPING_ZONES(state, zones) {
    state.shippingZones = zones;
  },
  SET_PACKAGE_BUILDER(state, data) {
    state.packageBuilder[data.step] = data.productData;
  },
  SET_SUBSCRIPTION_DISPLAY_PRICE(state, { key, currency, amount }) {
    Vue.set(state.subscriptionDisplayPrices, `${key}|${currency}`, amount);
  },
};

export const actions = {
  refreshCartProductPrices,
  async getJobPostings(context) {
    await JobPostingsService.getJobPostings().then((response) => {
      context.commit('ADD_JOB_POSTINGS', response.data);
    });
  },
  initializeApp(context) {
    if (process.client && window) {
      runLocalToggleOverrideInitializeEffect(context, window.location.search);
      runOneTrustEffect(window);
    }
    context.commit('SET_APP_INITIALIZED', true);
  },
  async getProducts({ commit }) {
    const resp = await this.$eCommService.getProducts();
    commit('SET_PRODUCTS', resp);
  },
  async getCollection({ commit, state }, { id, country = null }) {
    try {
      // Use explicit country if provided, otherwise fall back to state
      // This avoids reactive dependency issues when called with explicit country
      const selectedCountry = country || state.geoLocationCountry || 'US';
      const resp = await this.$eCommService.getCollection({ id, country: selectedCountry });
      commit('SET_COLLECTION', resp);
    } catch (err) {
      BugsnagWrapper.getInstance({ experiments: state.experiments }).notify(err);
    }
  },
  async getNamedCollection({ commit, state }, { id, name }) {
    try {
      const country = state.geoLocationCountry || 'US';
      const resp = await this.$eCommService.getCollection({ id, country });
      commit('SET_NAMED_COLLECTION', { name, collection: resp });
    } catch (err) {
      BugsnagWrapper.getInstance({ experiments: state.experiments }).notify(err);
    }
  },
  packageBuilderStep1({ commit }, data) {
    commit('SET_PACKAGE_BUILDER', data);
  },
  toggleDrawer({ commit, getters }) {
    if (!getters.isGQOrder) {
      const productList = getters.cartProducts.shopifyItems.map(item => ({
        item_id: item.variant.id,
        item_name: item.title,
        item_variant: item.variant.productName,
        price: item.variant.price,
        quantity: item.variant.qty,
        item_category: item.variant.type,
      }));

      this.$ecommAnalytics.view_cart(productList);
    }

    commit('toggleDrawer');
  },
  async ensureSubscriptionDisplayPrices({ getters, commit }, { billingPlanKeys, currency }) {
    const missing = billingPlanKeys.filter((k) => getters.subscriptionAmount(k, currency) == null);
    if (!missing.length) return;

    const resp = await axios.post(`${process.env.VUE_APP_API_URL}/subscription/display-prices`, {
      currency,
      billingPlanKeys: missing,
    });

    Object.entries(resp.data.prices).forEach(([key, obj]) => {
      commit('SET_SUBSCRIPTION_DISPLAY_PRICE', { key, currency, amount: obj.amount });
    });
  },
};

export const getters = {
  isShopifyOn: state => state.isShopifyOn,
  appVersion: (state) => state.appVersion,
  suppressModal: (state) => state.suppressModal,
  appInitialized: state => state.appInitialized && state.isGrowthbookInitialized,
  navType: state => state.navType,
  navigationData: state => state.navigationData,
  pageTransition: state => state.pageTransition,
  getExperimentById: state => id => getExperimentById(state, id),
  isFeatureOn: state => key => state.featureFlags.some(flag => flag.key === key && flag.isOn),
  showShopButton: state => state.showShopButton,
  isExperimentInitialized: state => id => state.experimentsInitialized[id],
  isInStoryblok: state => state.isInStoryblok,
  currentRoute: state => state.currentRoute,
  packagePageTotal: state => state.packagePageTotal,
  isGeoLocatedCanada: state => state.geoLocationCountry === 'CA',
  currency: (state, getters, rootState) => {
    // Shipping country takes precedence over geolocation country
    const country = rootState.checkout?.shipping?.country || state.geoLocationCountry;
    return country === 'CA' ? 'cad' : 'usd';
  },
  allProducts: state => state.products,
  productsCollection: state => state.productsCollection,
  collections: state => state.collections,
  getCollectionByName: state => name => state.collections[name] || [],
  subscriptionProducts: (_, getters) => getters.productsCollection.filter(product => product.productType === 'subscription'),
  packageProducts: (_, getters) => getters.productsCollection.filter(product => product.productType === 'package'),
  packageSelected: state => state.packageBuilder.package,
  variantSelected: state => state.packageBuilder.sizeVariant,
  subscriptionAmount: (state) => (key, currency) => {
    const result = state.subscriptionDisplayPrices[`${key}|${currency}`];
    return result != null ? result * 100 : null;
  },
  isExp041AnnualPrePurchaseVariant: (_, getters) => {
    const { variant } = getters.getExperimentById(experiments.EXP_041_ANNUAL_PRE_PURCHASE) || {};

    return variant === '1';
  },
};
